/**
 * Icelandic message bundle. Must mirror `en` exactly — `Messages` enforces it.
 * Translations land in task #8; until then every key falls back to English at
 * runtime via `t()`'s `?? en[key]` guard, so leaving entries equal to English
 * here is intentional and safe.
 */
import type { Messages } from "../index.ts";

export const is: Messages = {
  // App-level / document chrome
  "app.title": "Bead Blueprint",
  "app.description":
    "Ókeypis, auglýsingalaus perlumynstur. Skoðaðu safnið eða hannaðu þín eigin.",
  "app.version_free": "v0.1 · ókeypis",
  "app.tip_jar": "Þjórfé 🍕",
  "app.tip_jar_aria": "Senda þjórfé",
  "app.lang_label": "Tungumál",
  "app.lang_english": "English",
  "app.lang_icelandic": "Íslenska",

  // Gallery
  "gallery.title": "Bead Blueprint.",
  "gallery.count_one": "1 mynstur",
  "gallery.count_many": "{count} mynstur",
  "gallery.open_design": "Design →",
  "gallery.open_design_aria":
    "Opna Design til að hanna eða búa til úr ljósmynd",
  "gallery.categories_label": "Flokkar",
  "gallery.categories_aria": "Flokkar",
  "gallery.jump_label": "Flokkar",
  "gallery.jump_aria": "Sía eftir flokki",
  "gallery.category_filter_all": "Allir flokkar",
  "gallery.body_aria": "Mynstrasafn",
  "gallery.menu_aria": "Fleiri stillingar",
  "gallery.menu_close_aria": "Loka valmynd",
  "gallery.empty_headline": "Engin mynstur ennþá.",
  "gallery.empty_sub_html":
    "Settu TS skrá í <code>src/blueprints/&lt;flokkur&gt;/</code> og endurhladdu.",
  "gallery.empty_filter_match": "Engin mynstur passa við þessa síu.",
  "gallery.filter_label": "Litir",
  "gallery.filter_aria": "Sía eftir litafjölda",
  "gallery.filter_all": "Allt",
  "gallery.filter_one": "1 litur",
  "gallery.filter_two_three": "2-3 litir",
  "gallery.filter_four_plus": "4+ litir",

  // Categories
  "category.animals": "Dýr",
  "category.people": "Fólk",
  "category.vehicles": "Farartæki",
  "category.food": "Matur",
  "category.nature": "Náttúra",
  "category.space": "Geimurinn",
  "category.letters": "Stafir",
  "category.alphabet": "Stafróf",
  "category.numbers": "Tölur",
  "category.holidays": "Hátíðir",
  "category.emoji": "Emoji",
  "category.shapes": "Form",
  "category.other": "Annað",

  // Viewer
  "viewer.title_format": "{name}.",
  "viewer.back_to_gallery_aria": "Til baka í safn",
  "viewer.back_to_gallery": "← Safn",
  "viewer.meta_aria": "Upplýsingar um mynstur",
  "viewer.meta_board": "Plata",
  "viewer.meta_category": "Flokkur",
  "viewer.meta_beads": "Perlur",
  "viewer.meta_authored": "Höfundur",
  "viewer.swap_note": "Smelltu á lit í lista til að skipta út.",
  "viewer.preview_aria": "Forskoðun mynsturs",
  "viewer.grid_aria": "Forskoðun perlumynsturs",
  "viewer.legend_aria": "Litalisti",
  "viewer.open_in_design": "Opna í Design",
  "viewer.open_in_design_aria": "Opna þetta mynstur í Design",
  "viewer.export_pdf": "Flytja út PDF",
  "viewer.export_png": "Flytja út PNG",
  "viewer.copy_link": "Afrita hlekk",
  "viewer.copy_link_done": "Afritað",
  "viewer.exporting": "Flyt út…",
  "viewer.rendering": "Teiknar…",
  "viewer.chip_color_one": "{board} · 1 litur",
  "viewer.chip_color_many": "{board} · {count} litir",
  "viewer.authored_manual": "Hönd · handgert",
  "viewer.toast_not_found": "Mynstur fannst ekki.",
  "viewer.toast_pdf_failed": "PDF vistun mistókst — reyndu aftur.",
  "viewer.toast_png_failed": "PNG vistun mistókst — reyndu aftur.",
  "viewer.toast_link_copied": "Hlekkur afritaður.",
  "viewer.copy_prompt": "Afritaðu þessa slóð:",
  "viewer.shared_pattern_name": "Deilt mynstur",
  "viewer.toast_share_invalid":
    "Ekki var hægt að opna hlekkinn — gæti verið frá nýrri útgáfu.",
  "viewer.downscale_note":
    "Niðurkvörðuð frá {native} — útkoman er ófyrirsjáanleg, skoðaðu vel áður en þú prentar.",
  "viewer.downscale_note_fragmented":
    "Niðurkvörðun frá {native} braut mynstrið í {components} aðskilda hluta — þeir bráðna ekki saman. Veldu stærri plötu.",
  "viewer.board_native_aria": "{name} (upprunaleg stærð)",

  // Design — chrome
  "design.app_aria": "Bead Blueprint",
  "design.title": "Design.",
  "design.controls_aria": "Stillingar",
  "design.board_picker_aria": "Plötuval",
  "design.board_label": "Plata",
  "design.board_shape_aria": "Plötuform",
  "design.zoom_label": "Aðdráttur",
  "design.zoom_aria": "Aðdráttur",
  "design.zoom_level_aria": "Aðdráttarstig",
  "design.tools_aria": "Verkfæri",
  "design.tool_label": "Verkfæri",
  "design.tool_aria": "Verkfæri",
  "design.active_color_aria": "Virkur litur",
  "design.recent_colors_aria": "Nýlegir litir",
  "design.legend_toggle_label": "Litir í notkun",
  "design.legend_aria": "Litalisti",
  "design.history_aria": "Saga",
  "design.undo": "Til baka",
  "design.redo": "Áfram",
  "design.canvas_aria": "Strigi",
  "design.hint_local_only":
    "Drög lifa aðeins í þessum vafra — vistaðu til að deila.",
  "design.pattern_info_aria": "Mynstursupplýsingar",
  "design.design_tools_aria": "Design verkfæri",
  "design.setup_aria": "Stillingar",
  "design.canvas_label":
    "Perlustrigi. Notaðu örvalykla til að færa bendil, Enter til að mála, Backspace til að má.",

  // Design — tools, zoom
  "design.tool.paint": "Mála",
  "design.tool.fill": "Fylla",
  "design.tool.erase": "Má",
  "design.zoom.fit": "Passa",
  "design.zoom.1x": "1×",
  "design.zoom.2x": "2×",
  "design.zoom.3x": "3×",
  "design.zoom.4x": "4×",

  // Design — mirror toggles + icons
  "design.mirror_h_html":
    '<span class="design-toggle-text">Spegla </span><span class="design-toggle-glyph">↔</span>',
  "design.mirror_v_html":
    '<span class="design-toggle-text">Spegla </span><span class="design-toggle-glyph">↕</span>',
  "design.mirror_h_icon_label": "Spegla lárétt",
  "design.mirror_v_icon_label": "Spegla lóðrétt",

  // Design — export menu
  "design.export.trigger": "Flytja út",
  "design.export.aria": "Flytja út mynstur",
  "design.export.share_community": "Deila með samfélaginu",
  "design.export.pdf": "PDF — prentanleg perlukort",
  "design.export.png": "PNG — mynd",
  "design.export.json": "Teikning (JSON)",
  "design.export.json_copy": "Afrita teikningar-JSON",
  "design.export.copy_share_link": "Afrita deilihlekk",

  // Design — import / new menu
  "design.import.trigger": "Nýtt",
  "design.import.aria": "Nýtt mynstur",
  "design.import.blank": "Auð plata",
  "design.import.from_blueprint": "Úr teikningu (JSON)…",
  "design.import.from_image": "Úr mynd…",
  "design.import.adjust_image": "Stilla mynd…",

  // Design — toasts + confirms
  "design.toast.pattern_not_found": "Mynstur „{id}“ fannst ekki.",
  "design.toast.draft_save_failed":
    "Drög verða ekki vistuð í þessari vafrastillingu — vinnan helst ekki.",
  "design.toast.board_cleared": "Plata hreinsuð.",
  "design.toast.board_changed": "Plata: {name}",
  "design.toast.board_scaled": "Stækkað í {name}",
  "design.toast.draft_incompatible":
    "Vistuð drög voru ósamhæf — byrja upp á nýtt.",
  "design.toast.loaded_seed": "{name} hlaðið — haltu áfram.",
  "design.toast.pdf_exported_phone": "PDF flutt út — sjá Files eða opna flipa",
  "design.toast.pdf_failed": "Útflutningur PDF mistókst — reyndu aftur.",
  "design.toast.png_exported_phone": "PNG flutt út — sjá Files eða opna flipa",
  "design.toast.png_failed": "Útflutningur PNG mistókst — reyndu aftur.",
  "design.toast.json_exported": "Flutti út {filename}",
  "design.toast.json_copied": "Teikningar-JSON afritað á klippiborð",
  "design.toast.json_copy_failed": "Tókst ekki að afrita á klippiborð.",
  "design.toast.link_copied": "Deilihlekkur afritaður.",
  "design.toast.link_copy_failed":
    "Tókst ekki að afrita hlekk — reyndu Flytja út → JSON.",
  "design.toast.share_invalid":
    "Ekki var hægt að opna hlekkinn — gæti verið frá nýrri útgáfu.",
  "design.toast.share_pattern_too_large":
    "Mynstrið er of stórt fyrir hlekk — flyttu út PNG/PDF í staðinn.",
  "design.toast.share_submitted": "Sent til yfirferðar.",
  "design.toast.share_already_submitted": "Þegar sent til yfirferðar — engin þörf á að senda aftur.",
  "design.toast.share_submit_failed": "Tókst ekki að senda — reyndu aftur.",
  "design.copy_json_prompt": "Afritaðu þessa blueprint-JSON:",
  "design.export.default_name": "Ónefnt",
  "design.toast.read_failed": "Tókst ekki að lesa skrá.",
  "design.toast.import_failed": "Innflutningur mistókst: {error}",
  "design.toast.imported_one": "„{name}“ flutt inn",
  "design.toast.imported_with_warnings_one":
    "„{name}“ flutt inn (með 1 viðvörun)",
  "design.toast.imported_with_warnings_many":
    "„{name}“ flutt inn (með {count} viðvörunum)",
  "design.toast.imported_from_image":
    "Flutt inn úr mynd — málaðu yfir til að fínpússa.",
  "design.toast.image_unreadable":
    "Tókst ekki að lesa þessa mynd — reyndu aðra skrá.",
  "design.toast.drop_help": "Slepptu .bead.json skrá eða mynd til að byrja.",
  "design.confirm.replace_with_seed":
    "Hlaða „{name}“? Núverandi drög verða yfirskrifuð.",
  "design.confirm.switch_board_title": "Skipta yfir í {name}?",
  "design.confirm.switch_board_scale":
    "Stækka núverandi mynstur í {name}, eða byrja upp á nýtt? Afturkalla endurheimtir fyrra mynstur hvort heldur sem er.",
  "design.confirm.switch_board_downscale":
    "{name} hefur færri perlur — smáatriði tapast við minnkun. Afturkalla endurheimtir upprunalegt mynstur í fullri upplausn.",
  "design.confirm.switch_board_aspect_mismatch":
    "{name} hefur ólíkar hliðar — stækkun myndi bjaga mynstrið. Byrja upp á nýtt? Afturkalla endurheimtir fyrra mynstur.",
  "design.confirm.scale_to_fit": "Stækka",
  "design.confirm.start_fresh": "Byrja upp á nýtt",
  "design.confirm.cancel": "Hætta við",
  "design.confirm.continue": "Halda áfram",
  "design.confirm.share_after_export_title": "Deila með samfélaginu?",
  "design.confirm.share_after_export_message":
    "Manneskja fer yfir það handvirkt áður en það bætist við almenna safnið.",
  "design.confirm.share_after_export_confirm": "Deila",
  "design.confirm.share_after_export_cancel": "Nei takk",
  "design.confirm.replace_draft": "Skipta út drögum",
  "design.confirm.replace_with_seed_title": "Skipta út drögum?",
  "design.confirm.import_replaces_draft_title": "Skipta út drögum?",
  "design.confirm.image_replaces_draft_title": "Skipta út drögum?",
  "design.confirm.import_replaces_draft":
    "Innflutningur „{name}“ skrifar yfir núverandi drög. Halda áfram?",
  "design.confirm.image_replaces_draft":
    "Að búa til úr mynd skrifar yfir núverandi drög. Halda áfram?",

  // Design — accessibility live announcer
  "design.aria.tool_announce": "Verkfæri: {tool}",
  "design.aria.zoom_announce": "Aðdráttur: {name}",
  "design.aria.cell_announce": "Reitur {x}, {y}",
  "design.aria.painted_at": "Málaði {color} á reit {x}, {y}",
  "design.aria.erased_at": "Máði reit {x}, {y}",
  "design.aria.unknown_color": "óþekktur",
  "design.aria.switch_to_color": "Skipta í {name}",

  // Phone setup sheet
  "phone_setup.aria": "Stillingar",
  "phone_setup.import": "Flytja inn…",
  "phone_setup.from_image": "Úr mynd…",
  "phone_setup.clear": "Hreinsa plötu",
  "phone_setup.gallery": "← Til baka í safn",

  // Quantize modal
  "quantize.title": "Búa til úr mynd",
  "quantize.aria": "Búa til úr mynd",
  "quantize.drop_aria": "Slepptu mynd til að byrja",
  "quantize.drop_label_cursor": "Slepptu mynd",
  "quantize.drop_label_touch": "Smelltu til að velja",
  "quantize.drop_hint": "PNG · JPEG · WebP",
  "quantize.board_label": "Plata",
  "quantize.colors_label": "Litir",
  "quantize.colors_aria": "Hámarksfjöldi lita",
  "quantize.color_cap.all": "Allir",
  "quantize.preview_hint": "Slepptu mynd til að sjá forskoðun.",
  "quantize.legend_aria": "Litalisti",
  "quantize.cancel": "Hætta við",
  "quantize.confirm": "Nota þetta mynstur",
  "quantize.toast.decoding": "Afkóða {filename}…",
  "quantize.toast.image_unreadable":
    "Tókst ekki að lesa þessa mynd — reyndu aðra skrá.",
  "quantize.toast.processing_failed":
    "Vinnsla myndar mistókst — reyndu aðra mynd.",
  "quantize.portrait_nudge":
    "Er þetta andlitsmynd? Andlit koma betur út á 58×58 plötu.",
  "quantize.portrait_nudge_use_2x2": "Nota 58×58",
  "quantize.portrait_nudge_dismiss": "Halda núverandi",

  // Legend / colors-used panel
  "legend.colors_used_aria": "Litir í notkun",
  "legend.row_aria_one": "{name}, 1 perla. Smelltu til að skipta um lit.",
  "legend.row_aria_many":
    "{name}, {count} perlur. Smelltu til að skipta um lit.",

  // Color pickers
  "color_picker.aria": "Veldu lit",
  "color_picker.head": "Veldu lit",
  "swap_picker.aria": "Veldu lit í staðinn",
  "swap_picker.head": "Skipta {name} →",

  // Generic UI
  "ui.actions_aria": "Aðgerðir",
  "ui.options_aria": "Valkostir",

  // Validation
  "validation.no_file": "Engin skrá tilgreind",
  "validation.heic":
    "iPhone myndir: opnaðu í Photos og vistaðu sem JPEG, slepptu svo hér.",
  "validation.unsupported": "Slepptu mynd — PNG, JPEG eða WebP.",
  "validation.too_large": "Mynd of stór — reyndu undir 25 MB.",

  // Privacy page
  "privacy.link": "Persónuvernd",
  "privacy.link_aria": "Persónuverndarstefna",
  "privacy.title": "Persónuvernd.",
  "privacy.back": "← Safn",
  "privacy.back_aria": "Til baka í safn",
  "privacy.updated": "Uppfært 2026-08-04",

  "privacy.tldr_label": "Í stuttu máli",
  "privacy.tldr_body":
    "Bead Blueprint keyrir alfarið í vafranum þínum. Engir notendareikningar, engin myndasending á netþjón, ekkert selt um þig. Einu gögnin sem fara út af tækinu þínu eru ópersónuleg umferðatölfræði, þjórfé ef þú velur að gefa það, og — aðeins ef þú velur að deila hönnuninni með samfélaginu — mynstrið sem þú sendir inn til yfirferðar.",

  "privacy.section_data_label": "Hvað við söfnum",
  "privacy.data_images_head": "Myndir sem þú sleppir inn",
  "privacy.data_images_body":
    "Myndir sem þú sleppir inn eru afkóðaðar og unnar í vafranum þínum. Þær eru aldrei sendar á netþjón sem við stjórnum og við sjáum þær ekki.",
  "privacy.data_drafts_head": "Drög og stillingar",
  "privacy.data_drafts_body":
    "Núverandi hönnun og tungumálastilling eru vistuð í localStorage vafrans svo þau lifi af endurhleðslu. Þau eru aðeins á þínu tæki — að hreinsa vefgögn fjarlægir þau.",
  "privacy.data_analytics_head": "Ópersónuleg umferðatölfræði",
  "privacy.data_analytics_body":
    "Við notum Cloudflare Web Analytics til að telja síðuflettingar og sjá hvaða mynstur eru vinsæl. Það notar engar smákökur, fingrafarar ekki gesti og fylgir þér ekki milli vefja. Cloudflare vinnur IP-tölu og vafrastreng tímabundið til að sía bota; engin persónuleg gögn eru geymd eða seld.",
  "privacy.data_tips_head": "Þjórfé (valfrjálst)",
  "privacy.data_tips_body":
    "Ef þú velur að gefa þjórfé opnar tengillinn Polar.sh — greiðslumiðlarann okkar. Þeir meðhöndla nafn, netfang og kortaupplýsingar samkvæmt eigin persónuverndarstefnu. Við fáum aðeins yfirlit um greiðslur; við sjáum aldrei kortanúmerið þitt.",
  "privacy.data_submissions_head": "Innsendingar í samfélagssafn (valfrjálst)",
  "privacy.data_submissions_body":
    "Ef þú velur „Deila með samfélaginu“ í útflutningsvalmyndinni — eða svarar játandi þegar spurt er eftir annan útflutning — sendum við aðeins mynstrið sjálft, ekki myndina sem það var hugsanlega byggt á, á netþjóninn okkar til yfirferðar. Við geymum aðeins mynstrið, tímastimpil og stöðu yfirferðar: ekkert nafn, athugasemd, IP-tölu eða önnur persónugreinanleg gögn. Engin leið er til að rekja innsendingu til þín. Manneskja fer yfir hverja innsendingu — ekkert bætist sjálfkrafa við almenna safnið.",

  "privacy.section_dont_label": "Hvað við gerum ekki",
  "privacy.dont_accounts": "Engir notendareikningar eða innskráningar.",
  "privacy.dont_upload":
    "Við sendum, geymum eða deilum ekki myndum þínum — það eina sem við nokkurn tímann geymum er mynstur innsendrar hönnunar, og aðeins ef þú velur það.",
  "privacy.dont_sell": "Við seljum eða deilum engum gögnum með auglýsendum.",
  "privacy.dont_track": "Engar fylgismákökur eða milli-vefja auðkenni.",
  "privacy.dont_identify_submissions":
    "Við söfnum engu sem gæti persónugreint hver sendi inn hönnun í safnið.",

  "privacy.section_third_label": "Þriðju aðilar",
  "privacy.third_cloudflare": "Cloudflare — hýsing + ópersónuleg tölfræði.",
  "privacy.third_polar":
    "Polar.sh — greiðslur fyrir þjórfé (aðeins ef þú gefur þjórfé).",
  "privacy.third_fonts": "Google Fonts — afgreiðir leturgerðir viðmótsins.",

  "privacy.section_rights_label": "Réttindi þín",
  "privacy.rights_body":
    "Þar sem við geymum engin persónuleg gögn hjá okkur er ekkert fyrir okkur að fletta upp, flytja út eða eyða. Þú getur hreinsað staðbundin drög hvenær sem er í vefstillingum vafrans. Spurningar um þjórfé fara til greiðslumiðlarans (Polar.sh).",

  "privacy.section_contact_label": "Hafðu samband",
  "privacy.contact_body":
    "Spurningar um þessa stefnu? Sendu póst á birkirb@outlook.com.",

  // Heiti á innbyggðum teikningum — vísað í gegnum Blueprint.id.
  "blueprint_name.0": "0",
  "blueprint_name.1": "1",
  "blueprint_name.2": "2",
  "blueprint_name.3": "3",
  "blueprint_name.4": "4",
  "blueprint_name.5": "5",
  "blueprint_name.6": "6",
  "blueprint_name.7": "7",
  "blueprint_name.8": "8",
  "blueprint_name.9": "9",
  "blueprint_name.airplane": "Flugvél",
  "blueprint_name.alien": "Geimvera",
  "blueprint_name.ambulance": "Sjúkrabíll",
  "blueprint_name.astronaut": "Geimfari",
  "blueprint_name.bee": "Býfluga",
  "blueprint_name.bunny": "Kanína",
  "blueprint_name.bus": "Strætó",
  "blueprint_name.car": "Bíll",
  "blueprint_name.cat": "Köttur",
  "blueprint_name.christmas-tree": "Jólatré",
  "blueprint_name.circle": "Hringur",
  "blueprint_name.cloud": "Ský",
  "blueprint_name.crab": "Krabbi",
  "blueprint_name.cupcake": "Bollakaka",
  "blueprint_name.dog": "Hundur",
  "blueprint_name.donut": "Kleinuhringur",
  "blueprint_name.elephant": "Fíll",
  "blueprint_name.fire-truck": "Slökkviliðsbíll",
  "blueprint_name.fish": "Fiskur",
  "blueprint_name.flower": "Blóm",
  "blueprint_name.fox": "Refur",
  "blueprint_name.frog": "Froskur",
  "blueprint_name.girl": "Stelpa",
  "blueprint_name.heart": "Hjarta",
  "blueprint_name.helicopter": "Þyrla",
  "blueprint_name.ice-cream": "Ís",
  "blueprint_name.ladybug": "Maríuhæna",
  "blueprint_name.lightning": "Elding",
  "blueprint_name.mars-rocer": "Mars-jeppi",
  "blueprint_name.monkey": "Api",
  "blueprint_name.moon": "Tungl",
  "blueprint_name.mountains": "Fjöll",
  "blueprint_name.mushroom": "Sveppur",
  "blueprint_name.music-note": "Nóta",
  "blueprint_name.nut": "Hneta",
  "blueprint_name.owl": "Ugla",
  "blueprint_name.panda": "Pandabjörn",
  "blueprint_name.penguin": "Mörgæs",
  "blueprint_name.pickup": "Pallbíll",
  "blueprint_name.pizza": "Pítsa",
  "blueprint_name.planet": "Pláneta",
  "blueprint_name.plus": "Plús",
  "blueprint_name.police-car": "Lögreglubíll",
  "blueprint_name.pumpkin": "Grasker",
  "blueprint_name.rainbow": "Regnbogi",
  "blueprint_name.rocket": "Eldflaug",
  "blueprint_name.sailboat": "Seglbátur",
  "blueprint_name.shamrock": "Smári",
  "blueprint_name.shark": "Hákarl",
  "blueprint_name.side-profile-beagle": "Beagle (hliðarmynd)",
  "blueprint_name.side-profile-dachshund": "Dachshund (hliðarmynd)",
  "blueprint_name.side-profile-husky": "Husky (hliðarmynd)",
  "blueprint_name.smiley": "Broskall",
  "blueprint_name.snake": "Slanga",
  "blueprint_name.snowflake": "Snjókorn",
  "blueprint_name.star": "Stjarna",
  "blueprint_name.submarine": "Kafbátur",
  "blueprint_name.sun": "Sól",
  "blueprint_name.train": "Lest",
  "blueprint_name.tree": "Tré",
  "blueprint_name.turtle": "Skjaldbaka",
  "blueprint_name.ufo": "UFO",
  "blueprint_name.volcano": "Eldfjall",
  "blueprint_name.watermelon": "Vatnsmelóna",
  "blueprint_name.whale": "Hvalur",
  "blueprint_name.x": "X",
};
